import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RadixInput, RadixSelect } from '@/components/radix';
import { t } from 'i18next';
import { usePlaygroundFormRegistry } from '../../hooks/usePlaygroundFormRegistry';
import DaySelector, { type DaySelectorLabel } from './DaySelector';
import {
    buildCompositeFromDaySelectorValue,
    DURATION_HOURS_TOUCHED,
    getDaySelectorValueFromComposite,
} from './durationPeriodUtils';
import { DURATION_OPERATORS_WITHIN_NEXT } from '@/data/Marketing/marketingDurationOperators';

interface ClipCardBalanceCompositeValues {
    minClips: number | null;
    maxClips: number | null;
    expirationPeriod: [number | null, number | null];
    clipType?: 'min' | 'max';
    selectedDurationFormat?: DaySelectorLabel;
    operator?: string;
}

interface ClipCardBalanceInputProps {
    value: number | null | undefined;
    compositeValues?: ClipCardBalanceCompositeValues;
    conditionInstanceId: string;
}

const ClipCardBalanceInput = ({ value, compositeValues, conditionInstanceId }: ClipCardBalanceInputProps) => {
    const { register } = usePlaygroundFormRegistry();
    const minClips = compositeValues?.minClips || null;
    const maxClips = compositeValues?.maxClips || null;
    const initialDuration = getDaySelectorValueFromComposite(compositeValues, {
        includeOperator: true,
        defaultOperator: compositeValues?.operator ?? 'WITHIN_NEXT_HOURS',
    });

    // Determine initial clip type from compositeValues or existing values
    const initialClipType =
        compositeValues?.clipType || (minClips !== null ? 'min' : maxClips !== null ? 'max' : 'min');

    const formik = useFormik({
        initialValues: {
            clipType: initialClipType,
            minClips: minClips,
            maxClips: maxClips,
            duration: initialDuration,
        },
        validationSchema: Yup.object().shape({
            clipType: Yup.string().oneOf(['min', 'max']).required(),
            minClips: Yup.number()
                .nullable()
                .when('clipType', {
                    is: 'min',
                    then: (schema) =>
                        schema
                            .required(t('Marketing.MinClipsRequired'))
                            .typeError(t('Marketing.MinClipsMustBeNumber'))
                            .min(0, t('Marketing.MinClipsMustBePositive')),
                    otherwise: (schema) => schema.nullable(),
                }),
            maxClips: Yup.number()
                .nullable()
                .when('clipType', {
                    is: 'max',
                    then: (schema) =>
                        schema
                            .required(t('Marketing.MaxClipsRequired'))
                            .typeError(t('Marketing.MaxClipsMustBeNumber'))
                            .min(0, t('Marketing.MaxClipsMustBePositive')),
                    otherwise: (schema) => schema.nullable(),
                }),
            duration: Yup.object().shape({
                hours: Yup.number().test(
                    'required',
                    t('Marketing.DaysRequired'),
                    (val) => val !== null && val !== undefined,
                ),
            }),
        }),
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: () => {
            // Validation handled automatically
        },
    });

    useEffect(() => {
        register(conditionInstanceId, {
            validate: async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                    formik.setTouched({
                        duration: DURATION_HOURS_TOUCHED,
                        ...(formik.values.clipType === 'min' ? { minClips: true } : { maxClips: true }),
                    });
                    return false;
                }
                return true;
            },
            getValues: () => {
                const clipType = formik.values.clipType;
                const minClipsValue = clipType === 'min' ? formik.values.minClips : null;
                const maxClipsValue = clipType === 'max' ? formik.values.maxClips : null;
                const result = {
                    dualCondition: true,
                    clipType,
                    minClips: minClipsValue,
                    maxClips: maxClipsValue,
                    ...buildCompositeFromDaySelectorValue(formik.values.duration, 'expirationPeriod'),
                    expirationCondition: {
                        conditionId: 13,
                        value: formik.values.duration.hours,
                    },
                    clipsCondition: {
                        conditionId: 14,
                        minClips: minClipsValue,
                        maxClips: maxClipsValue,
                    },
                };
                return result;
            },
        });
        // Keep validator in registry on unmount so we can validate all at once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditionInstanceId, formik.values, register]);

    const handleMinClipsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const clips = e.target.value ? Number(e.target.value) : null;
        formik.setFieldValue('minClips', clips);
    };

    const handleMaxClipsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const clips = e.target.value ? Number(e.target.value) : null;
        formik.setFieldValue('maxClips', clips);
    };

    const handleClipTypeChange = (val: string) => {
        formik.setFieldValue('clipType', val);
        // Clear the non-selected field when switching
        if (val === 'min') {
            formik.setFieldValue('maxClips', null);
        } else {
            formik.setFieldValue('minClips', null);
        }
    };

    const isMinSelected = formik.values.clipType === 'min';

    return (
        <div className="flex flex-col gap-4">
            {/* Clip Type Select */}
            <div className="w-full">
                <label className="block text-sm font-medium text-text-primary mb-2">{t('Marketing.ClipType')}</label>
                <RadixSelect
                    options={[
                        { label: t('Marketing.MinClips'), value: 'min' },
                        { label: t('Marketing.MaxClips'), value: 'max' },
                    ]}
                    value={formik.values.clipType}
                    onValueChange={handleClipTypeChange}
                    placeholder={t('Marketing.SelectClipType')}
                    className="w-full"
                />
            </div>

            {/* Min Clips - only render if selected */}
            {isMinSelected && (
                <div className="w-full">
                    <label className="block text-sm font-medium text-text-primary mb-2">
                        {t('Marketing.MinClips')}
                    </label>
                    <RadixInput
                        type="number"
                        value={formik.values.minClips !== null ? String(formik.values.minClips) : ''}
                        onChange={handleMinClipsChange}
                        placeholder="0"
                        className="w-full"
                    />
                    {formik.touched.minClips && formik.errors.minClips && (
                        <div className="text-sm text-error-500 mt-1">
                            {typeof formik.errors.minClips === 'string'
                                ? formik.errors.minClips
                                : t('Marketing.MinClipsRequired')}
                        </div>
                    )}
                </div>
            )}

            {/* Max Clips - only render if selected */}
            {!isMinSelected && (
                <div className="w-full">
                    <label className="block text-sm font-medium text-text-primary mb-2">
                        {t('Marketing.MaxClips')}
                    </label>
                    <RadixInput
                        type="number"
                        value={formik.values.maxClips !== null ? String(formik.values.maxClips) : ''}
                        onChange={handleMaxClipsChange}
                        placeholder="0"
                        className="w-full"
                    />
                    {formik.touched.maxClips && formik.errors.maxClips && (
                        <div className="text-sm text-error-500 mt-1">
                            {typeof formik.errors.maxClips === 'string'
                                ? formik.errors.maxClips
                                : t('Marketing.MaxClipsRequired')}
                        </div>
                    )}
                </div>
            )}

            <DaySelector
                label={t('Marketing.ExpirationPeriod')}
                showOperator
                operatorOptions={DURATION_OPERATORS_WITHIN_NEXT}
                value={formik.values.duration}
                onChange={(next) => formik.setFieldValue('duration', next)}
                summaryNoteKey="Marketing.DaySelectorNoteClipCardExpiry"
                showError={Boolean(
                    formik.touched.duration &&
                        (formik.errors.duration as { hours?: string } | undefined)?.hours,
                )}
                error={
                    typeof (formik.errors.duration as { hours?: string } | undefined)?.hours === 'string'
                        ? (formik.errors.duration as { hours?: string }).hours
                        : t('Marketing.DaysRequired')
                }
            />
        </div>
    );
};

export default ClipCardBalanceInput;
